/**
 * Shared rating qualifier thresholds (0–10 scale).
 * Used for TripAdvisor-style review labels in activities and hotels (e.g. RBC).
 */

export enum RatingQualifierKey {
  Exceptional = "exceptional",
  Superb = "superb",
  Excellent = "excellent",
  VeryGood = "veryGood",
  Good = "good",
  Pleasant = "pleasant",
  Satisfactory = "satisfactory",
  Fair = "fair",
  Adequate = "adequate",
  Mediocre = "mediocre",
  BelowAverage = "belowAverage",
  Poor = "poor",
  VeryPoor = "veryPoor",
}

const RATING_THRESHOLDS_0_10: Array<{
  minExclusive: number;
  key: RatingQualifierKey;
  label: string;
}> = [
  {
    minExclusive: 9.8,
    key: RatingQualifierKey.Exceptional,
    label: "Exceptional",
  },
  { minExclusive: 9.5, key: RatingQualifierKey.Superb, label: "Superb" },
  { minExclusive: 9.0, key: RatingQualifierKey.Excellent, label: "Excellent" },
  { minExclusive: 8.5, key: RatingQualifierKey.VeryGood, label: "Very Good" },
  { minExclusive: 8.0, key: RatingQualifierKey.Good, label: "Good" },
  { minExclusive: 7.5, key: RatingQualifierKey.Pleasant, label: "Pleasant" },
  {
    minExclusive: 7.0,
    key: RatingQualifierKey.Satisfactory,
    label: "Satisfactory",
  },
  { minExclusive: 6.5, key: RatingQualifierKey.Fair, label: "Fair" },
  { minExclusive: 6.0, key: RatingQualifierKey.Adequate, label: "Adequate" },
  { minExclusive: 5.5, key: RatingQualifierKey.Mediocre, label: "Mediocre" },
  {
    minExclusive: 5.0,
    key: RatingQualifierKey.BelowAverage,
    label: "Below Average",
  },
  { minExclusive: 4.5, key: RatingQualifierKey.Poor, label: "Poor" },
];

/**
 * Returns the qualifier key for the given rating (0–10 scale).
 * Use for i18n: e.g. `t(\`namespace.ratingDescription.${key}\`)`
 */
export const getRatingQualifierKey = (
  ratingOutOf10: number
): RatingQualifierKey => {
  const match = RATING_THRESHOLDS_0_10.find(
    (t) => ratingOutOf10 > t.minExclusive
  );
  return match ? match.key : RatingQualifierKey.VeryPoor;
};

/**
 * Returns the English qualifier label for the given rating (0–10 scale).
 * Use when displaying the label directly (e.g. RBC hotels).
 */
export const getRatingQualifierLabel = (ratingOutOf10: number): string => {
  const match = RATING_THRESHOLDS_0_10.find(
    (t) => ratingOutOf10 > t.minExclusive
  );
  return match ? match.label : "Very Poor";
};
